import { useTranslation } from 'react-i18next';

/** Non-component helpers of the public site (kept apart for React fast refresh). */

/** The visitor's language version of a `{ fr, en }` content module from @gg/shared. */
export function useSiteContent<C extends { fr: unknown; en: unknown }>(
  content: C,
): C['fr'] | C['en'] {
  const { i18n } = useTranslation();
  return i18n.language === 'en' ? content.en : content.fr;
}

/** "{{name}}" placeholders of the site content modules. */
export const fill = (s: string, vars: Record<string, string | number>) =>
  s.replace(/\{\{(\w+)\}\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));

const ctaBase =
  'inline-flex items-center justify-center gap-2.5 font-site-display font-semibold uppercase tracking-[0.16em] no-underline transition-colors';
const ctaVariants = {
  clay: 'bg-site-clay text-site-cream hover:bg-site-clay-deep hover:text-site-cream',
  night: 'bg-site-night text-site-cream hover:bg-site-clay-deep hover:text-site-cream',
  ghost:
    'border border-site-cream/45 text-site-cream/90 font-medium hover:border-site-cream hover:text-site-cream',
  outline:
    'border border-site-line-input text-site-ink font-medium hover:border-site-ink hover:text-site-ink',
} as const;
const ctaSizes = {
  sm: 'min-h-11 px-4 text-xs md:px-5.5 md:text-sm',
  md: 'min-h-12.5 px-6.5 text-sm',
  lg: 'min-h-14 px-7.5 text-base',
} as const;

export type CtaVariant = keyof typeof ctaVariants;
export type CtaSize = keyof typeof ctaSizes;
export const ctaClasses = (variant: CtaVariant = 'clay', size: CtaSize = 'md') =>
  `${ctaBase} ${ctaVariants[variant]} ${ctaSizes[size]}`;
